import React, { FunctionComponent, SVGProps, useMemo, useState } from "react";
import styled from "styled-components";
import ToggleButtons from "../ToggleButton";
import Accordion, { AccordionProps } from "ui/Accordion";
import { breakpoints } from "themes/breakpoints";
import RegulationSection from "domain/public/Landing/AllInformations/components/RegulationSection";
import { Tab } from "interfaces/content";
import { spacing } from "globalStyles";
import Heading from "ui/Text/Heading";

export interface TabProps {
  label: string;
  items: AccordionProps["items"];
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  regulation?: Tab["regulation"] | null;
  image?: {
    title?: string;
    alt: string;
    path: string;
    width?: string;
  };
}

interface ScreenTabsProps {
  tabs: Array<TabProps>;
  className?: string;
  buttonWidth?: number;
  buttonHeight?: number;
  title?: string;
}

const ToggleTabs: React.FC<ScreenTabsProps> = ({
  tabs,
  className,
  buttonWidth,
  buttonHeight,
  title,
}) => {
  const [selectedTab, setSelectedTab] = useState("");

  const options = useMemo(
    () => tabs.map((tab) => ({ label: tab.label, value: tab.label, Icon: tab.icon })),
    [tabs],
  );

  const selectedTabValue = useMemo(
    () => selectedTab || options[0].value,
    [options, selectedTab],
  );

  const activeTab = useMemo(
    () => tabs.find((tab) => tab.label === selectedTabValue),
    [selectedTabValue, tabs],
  );

  const tabItems = useMemo(() => activeTab?.items, [activeTab]);

  return (
    <Container className={className}>
      {title && (
        <TabTitle align="center" variant="h3" color="secondary" fontWeight="light">
          {title}
        </TabTitle>
      )}
      <ToggleButtons
        onClick={setSelectedTab}
        options={options}
        selectedValue={selectedTabValue}
        buttonWidth={buttonWidth}
        buttonHeight={buttonHeight}
      />
      {((tabItems && tabItems.length > 0) || activeTab?.image) && (
        <div className="tab-content">
          {tabItems && tabItems.length > 0 && <Accordion items={tabItems} />}
          {activeTab?.regulation && activeTab.regulation.length > 0 && (
            <RegulationContainer className="mt-lg">
              {activeTab.regulation.map((regulation) => (
                <RegulationSection
                  key={`${regulation.label}_${regulation.path}`}
                  regulation={regulation.path}
                  label={regulation.label}
                />
              ))}
            </RegulationContainer>
          )}
          {activeTab?.image && (
            <>
              {activeTab.image.title && (
                <ImageTitle align="center" variant="h6" color="secondary">
                  {activeTab.image.title}
                </ImageTitle>
              )}
              <img
                className="mt-lg"
                src={`${process.env.REACT_APP_BASE_RELATIVE_ROUTES}/assets/${activeTab.image.path}`}
                alt={activeTab.image.alt}
                width={activeTab.image.width}
              />
            </>
          )}
        </div>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const TabTitle = styled(Heading)`
  margin-top: ${spacing.extraLarge}px;
  margin-bottom: ${spacing.large}px;
`;

const ImageTitle = styled(Heading)`
  margin-top: ${spacing.large}px;
`;

const RegulationContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 0;
  align-items: center;
  padding-inline: ${spacing.medium}px;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 2fr 1fr;
  }

  .regulation-button {
    max-width: 105px;
  }
`;

export default ToggleTabs;
